import React,{useState,useEffect} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useSupabase} from '../contexts/SupabaseContext';
import {useAuth} from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const {FiPlus,FiEdit3,FiTrash2,FiEye,FiCopy,FiActivity}=FiIcons;

const Dashboard=()=> {
  const [campaigns,setCampaigns]=useState([]);
  const [loading,setLoading]=useState(true);
  const supabase=useSupabase();
  const {user}=useAuth();

  useEffect(()=> {
    fetchCampaigns();
  },[]);

  const fetchCampaigns=async ()=> {
    try {
      const {data,error}=await supabase
        .from('campaigns')
        .select('*')
        .eq('owner_id',user.id)
        .order('created_at',{ascending: false});
      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      toast.error('Failed to fetch campaigns');
      console.error('Error:',error);
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign=async (id)=> {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      const {error}=await supabase
        .from('campaigns')
        .delete()
        .eq('id',id)
        .eq('owner_id',user.id);
      if (error) throw error;
      setCampaigns(campaigns.filter(c=> c.id !==id));
      toast.success('Campaign deleted successfully');
    } catch (error) {
      toast.error('Failed to delete campaign');
      console.error('Error:',error);
    }
  };

  const copyEmbedCode=(publicId)=> {
    // Ensure the embed URL always uses https
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'https:';
    const host = window.location.host;
    const embedUrl=`${protocol}//${host}/v1/c.js`;
    const embedCode=`<script src="${embedUrl}" data-cid="${publicId}" async></script>`;
    navigator.clipboard.writeText(embedCode);
    toast.success('Embed code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage your social proof campaigns</p>
            </div>
            <Link
              to="/campaigns/create"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
              New Campaign
            </Link>
          </div>

          {campaigns.length===0 ? (
            <motion.div
              initial={{opacity: 0,scale: 0.9}}
              animate={{opacity: 1,scale: 1}}
              transition={{duration: 0.5}}
              className="text-center py-12 bg-white rounded-lg shadow-sm"
            >
              <SafeIcon icon={FiActivity} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
              <p className="text-gray-600 mb-6">Create your first social proof campaign to get started</p>
              <Link
                to="/campaigns/create"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="h-5 w-5 mr-2" />
                Create Campaign
              </Link>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map((campaign,index)=> (
                <motion.div
                  key={campaign.id}
                  initial={{opacity: 0,y: 20}}
                  animate={{opacity: 1,y: 0}}
                  transition={{duration: 0.5,delay: index * 0.1}}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {campaign.name}
                      </h3>
                      <p className="text-sm text-gray-600">{campaign.product_name}</p>
                    </div>
                    <div
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        campaign.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {campaign.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Campaign ID:</p>
                    <div className="bg-gray-50 p-2 rounded text-xs font-mono text-gray-700 break-all">
                      {campaign.public_id}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <Link
                        to={`/campaigns/${campaign.id}`}
                        className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                        title="View details"
                      >
                        <SafeIcon icon={FiEye} className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/campaigns/${campaign.id}/edit`}
                        className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                        title="Edit campaign"
                      >
                        <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={()=> copyEmbedCode(campaign.public_id)}
                        className="p-2 text-gray-600 hover:text-green-600 transition-colors"
                        title="Copy embed code"
                      >
                        <SafeIcon icon={FiCopy} className="h-4 w-4" />
                      </button>
                      <button
                        onClick={()=> deleteCampaign(campaign.id)}
                        className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                        title="Delete campaign"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;